import { BottomSection, Content, FileBox, IconSection, MainDiv, UploadDiv } from './styles';
import TiptapEditor from '../../tiptapEditor';
import { CommentFilesInterface } from '../../../interfaces/TaskInterface';
import { UserInterface } from '../../../interfaces/chatMessageInterface';
import RecordingComment from '../../comment/recordingComment';
import Button from '../../button';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import {
  DEFAULT_UPLOAD_FOLDER,
  handleFileUpload,
  isMediaRecordingSupported,
  removeFileFromFirebase
} from '../../../helpers/common';
import { captureException } from '../../../services/logService';
import { RenderFilePreview } from '../../renderFilePreview';
import Modal from '../modal';
import InsertGifModal from '../insertGifModal';
import { Dropdown } from '../../dropdown';
import CommentDropdown from '../../dropdowns/commentDropdown';
import UserPreferenceSingleton from '../../../helpers/userPreferenceSingleton';
import Emoji from '../../emojiPickerDropdown';
import { EmojiClickData } from 'emoji-picker-react';
import { dispatchTaskError } from '../../../services/taskServices';
import { rootStore } from '../../../mobx/rootStore';
import { TiptapEditorRef } from '../../../interfaces/tiptapInterface';
import { observer } from 'mobx-react-lite';
import { BUTTON_TYPE, ICON_BUTTON_SIZE, ICON_BUTTON_TYPE } from '../../../global/constants';
import IconButton from '../../button/iconButton';
import ModalHeader from '../modalHeader';
import { getMentionUsersWithEveryone } from '../../../helpers/mentionHelper';

interface Props {
  onClose: () => void;
  sendComment: (files: CommentFilesInterface[], comment: string) => void;
  users: UserInterface[];
  uploadedFiles: CommentFilesInterface[];
  setUploadedFiles: (
    files: CommentFilesInterface[] | ((previousFiles: CommentFilesInterface[]) => CommentFilesInterface[])
  ) => void;
  setValueContent: (content: string) => void;
  propsLoading: boolean;
  setPropsLoading: (value: boolean) => void;
  onClickSelectFile?: () => void;
  handleOpenSnapModal: () => void;
  disable: boolean;
  valueContent: string;
  isEditMessage?: boolean;
  headerText?: string;
  isEveryoneMentionEnable?: boolean;
  excludeCurrentUser?: boolean;
  uploadFolder?: string;
}

const CommentModal = forwardRef((props: Props, ref) => {
  const {
    onClose,
    sendComment,
    users,
    uploadedFiles,
    setUploadedFiles,
    setValueContent,
    propsLoading,
    setPropsLoading,
    onClickSelectFile,
    handleOpenSnapModal,
    disable = false,
    valueContent,
    isEditMessage,
    headerText,
    isEveryoneMentionEnable,
    excludeCurrentUser,
    uploadFolder = DEFAULT_UPLOAD_FOLDER
  } = props;

  // Mobx store variables
  const { projectStore, currentUser } = rootStore;
  const { uploadFilePercent } = projectStore;

  // Messages exclude the current user from mentions (excludeCurrentUser); task comments keep them.
  const membersForMention = excludeCurrentUser ? users?.filter((user) => user?.id !== currentUser?.id) : users;

  // Prepend the "Everyone" mention option when enabled — e.g. navbar/project message edit.
  const usersWithAll = isEveryoneMentionEnable
    ? getMentionUsersWithEveryone(users, currentUser?.id)
    : membersForMention;

  //Refs
  const editorRef = useRef<TiptapEditorRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement | null>(null);

  //States
  const [loading, setLoading] = useState<boolean>(false);
  const [buttonDisable, setButtonDisable] = useState<boolean>(false);
  const [isEmojiDropdownOpen, setIsEmojiDropdownOpen] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [openGifModal, setIsOpenGifModal] = useState(false);

  // Ref functions which are call from parent component
  useImperativeHandle(ref, () => ({
    addRecordingCard(fileUrl: string, fileName: string) {
      editorRef.current?.addRecordingCard(fileUrl, fileName);
    },
    addImageInContent(fileUrl: string) {
      editorRef.current?.addImage(fileUrl);
    },
    clearContent() {
      editorRef?.current?.clearValue();
    }
  }));

  const uploadSnapEmbed = useCallback((firebaseUrl: string, fileName: string, videoId: string) => {
    editorRef?.current?.addRecordingCard(firebaseUrl, fileName, videoId);
  }, []);

  // On select emoji
  const handleEmojiSelected = useCallback((emojiData: EmojiClickData) => {
    const currentEmoji = UserPreferenceSingleton.getInstance().getUserUseEmoji();
    const updatedEmojis = [
      emojiData?.emoji,
      ...(Array.isArray(currentEmoji) ? currentEmoji.filter((e) => e !== emojiData?.emoji) : [])
    ]?.slice(0, 2);
    UserPreferenceSingleton.getInstance().setUserUseEmoji(updatedEmojis);
    editorRef.current?.addContentExistingLine(emojiData.emoji);
  }, []);

  const openEmojiDropdown = useCallback((value: boolean) => {
    setIsEmojiDropdownOpen(value);
  }, []);

  const onSendComment = useCallback(() => {
    setUploadedFiles([]);
    setValueContent('');
    sendComment(uploadedFiles, valueContent);
  }, [sendComment, setUploadedFiles, setValueContent, uploadedFiles, valueContent]);

  const onFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;
      if (!files || files?.length === 0) {
        dispatchTaskError('No files selected.');
        return;
      }
      try {
        setFileUploading(true);
        const fileArray = Array.from(files); // Convert FileList to Array

        //call common function to upload file in firebase
        const updatedFiles = await handleFileUpload(uploadedFiles || [], fileArray, editorRef, undefined, uploadFolder);
        const value = [...(uploadedFiles || []), ...(updatedFiles || [])];
        setUploadedFiles(value);
      } catch (error) {
        captureException(error);
        dispatchTaskError('Error occurred while uploading files.');
      } finally {
        setFileUploading(false);
      }
    },
    [setUploadedFiles, uploadedFiles, uploadFolder]
  );

  const onClickClose = useCallback(
    (index: number, file: CommentFilesInterface) => {
      if (file && !file.uploadFileType && file.href.startsWith('https://firebasestorage.googleapis'))
        removeFileFromFirebase(file.href);
      const filesClone = JSON.parse(JSON.stringify(uploadedFiles));
      filesClone.splice(index, 1);
      setUploadedFiles(filesClone);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [uploadedFiles, setUploadedFiles]
  );

  const handleFileInput = useCallback(() => {
    queueMicrotask(() => {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    });
  }, []);

  const addDroppedFiles = useCallback(
    (files: CommentFilesInterface[]) => {
      setUploadedFiles((previousFiles: CommentFilesInterface[]) => [...(previousFiles || []), ...(files || [])]);
    },
    [setUploadedFiles]
  );

  const handleOpenGifModal = useCallback(() => {
    setIsOpenGifModal(true);
  }, []);

  const handleCloseGifModal = useCallback(() => {
    setIsOpenGifModal(false);
  }, []);

  //ctrl + enter click update the task details
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        if (loading || propsLoading || buttonDisable) return;
        onSendComment();
      }
    },
    [buttonDisable, loading, onSendComment, propsLoading]
  );

  return (
    <>
      <MainDiv>
        <ModalHeader title={headerText} onClose={onClose} />
        <Content onKeyDown={handleKeyDown}>
          <TiptapEditor
            ref={editorRef}
            valueContent={valueContent}
            setValueContent={setValueContent}
            users={usersWithAll}
            placeholder='Leave a comment...'
            upload={{
              setLoading: (value: boolean) => {
                if (setPropsLoading) setPropsLoading(value);
                else setLoading(value);
              },
              setStateLoading: (value: boolean) => setFileUploading(value),
              addUploadedFiles: addDroppedFiles,
              uploadFolder
            }}
            toolbarActions={{
              recordSnapModalOpen: handleOpenSnapModal,
              handleFileInput,
              handleGifModal: handleOpenGifModal
            }}
            dropBox={{ isVisible: true }}
          />
          <input type='file' ref={fileInputRef} hidden onChange={onFileUpload} multiple aria-label='Upload file' />
          {!disable && uploadedFiles?.length > 0 && (
            <FileBox>
              {uploadedFiles?.length > 0 &&
                uploadedFiles?.map((file: CommentFilesInterface, index: number) => {
                  return (
                    <RenderFilePreview
                      key={file?.href}
                      file={file}
                      isClose={true}
                      maxWidth={211}
                      onClose={() => onClickClose(index, file)}
                    />
                  );
                })}
            </FileBox>
          )}
          {fileUploading && <UploadDiv>Uploading ({Math.floor(uploadFilePercent)}%)</UploadDiv>}
          <BottomSection>
            <IconSection isEditMessage={isEditMessage}>
              <Dropdown
                activeClassName='active'
                content={
                  <IconButton
                    iconName='file-attachment-icon'
                    size={isEditMessage ? ICON_BUTTON_SIZE.LARGE : ICON_BUTTON_SIZE.MEDIUM}
                    variant={ICON_BUTTON_TYPE.TERTIARY}
                  />
                }>
                <CommentDropdown
                  onClickUploadFile={handleFileInput}
                  onClickSelectFile={onClickSelectFile}
                  isSelectFileEnable={onClickSelectFile ? true : false}
                />
              </Dropdown>
              {isMediaRecordingSupported() && !isEditMessage && (
                <RecordingComment
                  setLoading={(value: boolean) => setLoading(value)}
                  fieldValue={valueContent}
                  setValueContent={(value: string) => setValueContent(value)}
                  isCommentModel={true}
                  setButtonDisable={(value: boolean) => setButtonDisable(value)}
                  handleOpenModal={handleOpenSnapModal}
                  uploadSnapEmbed={uploadSnapEmbed}
                />
              )}
              <Dropdown
                isMinWidth={258}
                isRemoveMaxHeight={true}
                content={
                  <IconButton
                    iconName='updated-emoji-icon'
                    size={isEditMessage ? ICON_BUTTON_SIZE.LARGE : ICON_BUTTON_SIZE.MEDIUM}
                    variant={ICON_BUTTON_TYPE.TERTIARY}
                    onClick={() => openEmojiDropdown(!isEmojiDropdownOpen)}
                  />
                }
                trigger='click'>
                <Emoji
                  onEmojiClick={handleEmojiSelected}
                  emojiButtonRef={emojiPickerRef}
                  isOpenEmoji={false}
                  setIsOpenEmoji={setIsEmojiDropdownOpen}
                  isCommentModal={true}
                  isEmojiModal={true}
                />
              </Dropdown>
            </IconSection>
            <Button
              title='Update'
              variant={BUTTON_TYPE.PRIMARY}
              onClick={onSendComment}
              disabled={loading || propsLoading || buttonDisable}
              size={isEditMessage ? 'medium' : 'small'}
            />
          </BottomSection>
        </Content>
      </MainDiv>
      <Modal open={openGifModal} onClose={handleCloseGifModal} maxWidth='686px' isResponsiveMaxWidth={true}>
        <InsertGifModal
          setSelectedGif={(url: string) => {
            editorRef.current?.addImage(url);
          }}
          closeModal={handleCloseGifModal}
        />
      </Modal>
    </>
  );
});

export default observer(CommentModal);
